import { BadgePercent, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { confirmModal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { fmtDate } from '@/features/shared/format'
import { apiErrorMessage } from '@/i18n'
import { useAssignIncentive, useEmployeeIncentives, useIncentives, usePayrollParams, useUnassignIncentive } from '../../api'
import type { PersonelProfile } from '../../model'
import { SectionCard } from '../SectionCard'

/**
 * Personelin SGK durumu: sigorta türü (personel formundan) ve dönemsel SGK teşvikleri. Teşvik atanmamış SGK'lı
 * personelde bordro parametrelerindeki varsayılan indirim uygulanır; Bağkur ve prim ödemeyen emeklide teşvik olmaz.
 */
export function SgkTesvikCard({ personelId, sigorta, canWrite }: { personelId: string; sigorta?: PersonelProfile['sigorta']; canWrite: boolean }) {
  const { t } = useTranslation()
  const insured = !sigorta || sigorta === 'sgk'
  const assigned = useEmployeeIncentives(personelId, insured)
  const catalog = useIncentives(insured && canWrite)
  const params = usePayrollParams(insured)
  const assign = useAssignIncentive(personelId)
  const unassign = useUnassignIncentive()
  const [adding, setAdding] = useState<{ incentiveId: string; start: string; end: string } | null>(null)
  const latest = [...(params.data ?? [])].sort((a, b) => b.validFrom.localeCompare(a.validFrom))[0]
  const today = new Date().toLocaleDateString('sv-SE')
  const rows = assigned.data ?? []
  const current = rows.filter((r) => r.start <= today && (!r.end || r.end >= today))
  const effective = current.length ? Math.max(...current.map((r) => Number(r.rate) || 0)) : null

  const submit = () => {
    if (!adding?.incentiveId || !adding.start || (adding.end && adding.end < adding.start)) {
      toast.error(t('hr.personel.tesvik.assignInvalid'))
      return
    }
    assign.mutate(adding, {
      onSuccess: () => {
        toast.success(t('hr.personel.tesvik.assigned_ok'))
        setAdding(null)
      },
      onError: (e) => toast.error(apiErrorMessage(e)),
    })
  }

  return (
    <SectionCard
      title={t('hr.personel.tesvik.cardTitle')}
      action={
        insured && canWrite && !adding ? (
          <Button size="sm" variant="outline" onClick={() => setAdding({ incentiveId: '', start: today.slice(0, 8) + '01', end: '' })}>
            <Plus />
            {t('hr.personel.tesvik.assign')}
          </Button>
        ) : null
      }
    >
      <div className="space-y-3 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground">{t('hr.personel.fields.sigorta')}:</span>
          <Badge tone={insured ? 'info' : 'neutral'}>{t(`hr.personel.form.options.sigorta.${sigorta ?? 'sgk'}`)}</Badge>
        </div>
        {!insured ? (
          <p className="text-xs text-muted-foreground">{t(sigorta === 'bagkur' ? 'hr.personel.tesvik.bagkurNote' : 'hr.personel.tesvik.retiredNote')}</p>
        ) : (
          <>
            <p className="text-xs text-muted-foreground">
              {effective == null
                ? t('hr.personel.tesvik.defaultApplies', { rate: latest?.employerDiscountRate || '—' })
                : t('hr.personel.tesvik.effective', { rate: effective })}
            </p>
            {rows.length ? (
              <ul className="divide-y rounded-xl border">
                {rows.map((r) => (
                  <li key={r.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
                    <BadgePercent className="size-4 text-primary" aria-hidden />
                    <span className="min-w-0 flex-1 font-medium">
                      {r.code} · {r.name}
                    </span>
                    <span className="tabular-nums text-muted-foreground">%{r.rate}</span>
                    <span className="text-xs text-muted-foreground">
                      {fmtDate(r.start)} – {r.end ? fmtDate(r.end) : t('hr.personel.tesvik.openEnded')}
                    </span>
                    {canWrite ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7 text-muted-foreground hover:text-destructive"
                        aria-label={t('common.delete')}
                        onClick={() =>
                          void confirmModal({ title: t('hr.personel.tesvik.unassignTitle'), description: `${r.code} · ${r.name}`, confirmLabel: t('common.delete'), destructive: true }).then(
                            (ok) => ok && unassign.mutate(r.id, { onError: (e) => toast.error(apiErrorMessage(e)) }),
                          )
                        }
                      >
                        <Trash2 />
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : null}
            {adding ? (
              <div className="grid gap-2 rounded-xl border bg-muted/30 p-3 sm:grid-cols-3">
                <Select
                  searchable
                  value={adding.incentiveId}
                  onChange={(v) => setAdding({ ...adding, incentiveId: v })}
                  placeholder={t('hr.personel.tesvik.pick')}
                  aria-label={t('hr.personel.tesvik.pick')}
                  options={(catalog.data ?? []).filter((i) => i.active).map((i) => ({ value: i.id, label: `${i.code} · ${i.name}`, description: `%${i.rate}` }))}
                />
                <DateField value={adding.start} onChange={(v) => setAdding({ ...adding, start: v })} aria-label={t('hr.personel.tesvik.start')} placeholder={t('hr.personel.tesvik.start')} clearable={false} />
                <DateField value={adding.end} min={adding.start || undefined} onChange={(v) => setAdding({ ...adding, end: v })} aria-label={t('hr.personel.tesvik.end')} placeholder={t('hr.personel.tesvik.endPh')} />
                <div className="flex justify-end gap-2 sm:col-span-3">
                  <Button size="sm" variant="cancel" onClick={() => setAdding(null)}>
                    {t('common.cancel')}
                  </Button>
                  <Button size="sm" disabled={assign.isPending} onClick={submit}>
                    {t('common.save')}
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </SectionCard>
  )
}
